import { window } from "global";
import { useEffect, useMemo } from "react";
import { MediaQuery } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { TierListHeader } from "@ugg/shared/components/TierList/TierListHeader";
import { TierListPageNav } from "@ugg/shared/components/TierList/TierListPageNav";
import { TierListFilterManager } from "@ugg/shared/components/TierList/TierListFilterManager";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { QueueTypeC, QueueTypeN } from "@ugg/shared/utils/queue-type-helpers";
import { useAramMayhemChampionTierList } from "@ugg/shared/hooks/aram-mayhem";

import { ALL_TIERS, TierNameValue } from "@ugg/shared/utils/tier-helpers";
import { TierGroupCard } from "@ugg/shared/components/TierList/common/TierGroupCard";
import { useValidatedTierListParams } from "@ugg/shared/components/TierList/common/use-validated-tier-list-params";
import { AD_RAIL_MIN_CONTENT_HEIGHT } from "@ugg/shared/components/TierList/common/min-content-height";
import { asArray, asText } from "@ugg/shared/utils/safe-coerce";

type TierListContainerProps = {
  bannerSlot?: React.ReactNode;
  patchNotesCTA?: React.ReactNode;
};

// Module-level so the array identity is stable across renders rather than a
// fresh literal on every row.
const SEARCH_PARAM_NOT_CARRIED = ["search"];

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemChampionTierListTableProps {
  // Partial: an absent tier is a legitimate payload shape, so the table
  // guards each one before mapping (see AramMayhemChampionTierList).
  tierlist: Partial<Record<TierNameValue, number[]>>;
}

export function AramMayhemChampionTierListTable(props: AramMayhemChampionTierListTableProps) {
  const { tierlist } = props || {};
  const convertedQueueType = QueueTypeC.convertToString(QueueTypeN.ARAM_MAYHEM) as string;

  return (
    <div className="flex flex-col gap-6">
      {ALL_TIERS.map((tier) => {
        // asArray: the feed is pipeline-produced and its types are an
        // assertion about a remote file, so a wrong-typed tier degrades to
        // empty rows instead of throwing on .map during render.
        const champions = asArray(tierlist[tier]);
        if (champions.length === 0) return null;
        return (
          <TierGroupCard key={tier} tier={tier}>
            {champions.map((championId) => (
              <ChampionLink
                key={championId}
                championId={championId}
                queueType={convertedQueueType}
                dim={36}
                // This page's `search` filters which champions the list shows.
                // The build page this links to defines its own `search` over a
                // different set of things, so letting it ride the link would
                // apply the champion query to that filter instead.
                omitParams={SEARCH_PARAM_NOT_CARRIED}
                className="[&_.champion-name]:!text-sm [&_.champion-name]:!ml-0 [&>div]:!px-0 !gap-[8px]"
              />
            ))}
          </TierGroupCard>
        );
      })}
    </div>
  );
}

export const AramMayhemChampionTierListContainer = ({ bannerSlot, patchNotesCTA }: TierListContainerProps) => {
  const { queryParams, validated } = useValidatedTierListParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST);

  const { data, loading, error } = useAramMayhemChampionTierList({ params: queryParams, ssr: true });
  const { tierlist, lastUpdated } = data || {};

  // Champion name search, mirroring the Classic tier list's. Driven by the
  // `search` query param (rendered by TierListFilterManager), so a filtered
  // view is shareable and survives reload. Names come from riot-assets — the
  // same source ChampionLink renders from — rather than a custom manifest,
  // since these are standard champion ids, not Classic's 60xxx namespace.
  const { getChampionName } = getRiotAssetsContext();
  const search = asText(validated.search);
  const searchedTierlist = useMemo(() => {
    const base = tierlist || {};
    const query = search.trim().toLowerCase();
    if (!query) return base;
    const result: Partial<Record<TierNameValue, number[]>> = {};
    for (const tier of ALL_TIERS) {
      result[tier] = asArray(base[tier]).filter((championId) => {
        const name = getChampionName(championId);
        return typeof name === "string" && name.toLowerCase().includes(query);
      });
    }
    return result;
  }, [tierlist, search, getChampionName]);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Server-rendered for SEO, mirroring the Classic tier list: the hook is
  // prefetched with `ssr: true`, so the server and the client's first render
  // read the same JSONFetcher cache entry and hydration matches.
  // `loading="lazy"` imgs inside are valid in SSR markup.
  let table;
  if (loading) {
    table = <TierListTablePlaceholder />;
  } else if (error) {
    table = <TierListTableError />;
  } else {
    table = <AramMayhemChampionTierListTable tierlist={searchedTierlist} />;
  }

  return (
    // Keeps the sticky ad rails from drifting up when the list filters down —
    // see AD_RAIL_MIN_CONTENT_HEIGHT.
    <div className={AD_RAIL_MIN_CONTENT_HEIGHT}>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={loading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot}
      <TierListFilterManager className="my-[24px]" />
      <>{table}</>
    </div>
  );
};
